import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { Webhook as WebhookIcon } from 'lucide-react'
import { useState } from 'react'
import type { Webhook, WebhookTestResult } from '@/api/hooks/use-webhooks'
import { useDeleteWebhook, useTestWebhook, useWebhooks } from '@/api/hooks/use-webhooks'
import { StatusBadge } from '@/components/ui/status-badge'
import { WebhookFormModal } from '@/pages/webhooks-form-modal'
import { WebhookLogsPanel } from '@/pages/webhooks-logs-panel'

export function WebhooksPage(): React.JSX.Element {
  const { data, isLoading } = useWebhooks()
  const deleteWebhook = useDeleteWebhook()
  const testWebhook = useTestWebhook()
  const [showForm, setShowForm] = useState(false)
  const [editTarget, setEditTarget] = useState<Webhook | null>(null)
  const [logsTarget, setLogsTarget] = useState<string | null>(null)
  const [testResults, setTestResults] = useState<Record<string, WebhookTestResult>>({})
  const [deleteTarget, setDeleteTarget] = useState<Webhook | null>(null)

  const webhooks = data?.data ?? []

  function handleTest(id: string): void {
    testWebhook.mutate(id, {
      onSuccess: (result) => setTestResults((prev) => ({ ...prev, [id]: result })),
    })
  }

  function handleDelete(): void {
    if (!deleteTarget) return
    deleteWebhook.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })
  }

  if (isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading webhooks...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">Webhooks</h1>
        <button
          type="button"
          onClick={() => {
            setEditTarget(null)
            setShowForm(true)
          }}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          + Add Endpoint
        </button>
      </div>

      {webhooks.length === 0 ? (
        <EmptyWebhooks onAddClick={() => setShowForm(true)} />
      ) : (
        <div className="space-y-4">
          {webhooks.map((wh) => (
            <WebhookCard
              key={wh.id}
              webhook={wh}
              testResult={testResults[wh.id]}
              onEdit={() => {
                setEditTarget(wh)
                setShowForm(true)
              }}
              onTest={() => handleTest(wh.id)}
              onLogs={() => setLogsTarget(wh.id)}
              onDelete={() => setDeleteTarget(wh)}
              isTesting={testWebhook.isPending}
            />
          ))}
        </div>
      )}

      {showForm && <WebhookFormModal webhook={editTarget} onClose={() => setShowForm(false)} />}
      {logsTarget && (
        <WebhookLogsPanel webhookId={logsTarget} onClose={() => setLogsTarget(null)} />
      )}
      {deleteTarget && (
        <DeleteConfirmation
          webhook={deleteTarget}
          isPending={deleteWebhook.isPending}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}

function WebhookCard({
  webhook,
  testResult,
  onEdit,
  onTest,
  onLogs,
  onDelete,
  isTesting,
}: {
  webhook: Webhook
  testResult?: WebhookTestResult
  onEdit: () => void
  onTest: () => void
  onLogs: () => void
  onDelete: () => void
  isTesting: boolean
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-text-primary">{webhook.url}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {webhook.events.map((evt) => (
              <StatusBadge key={evt} status="sent">
                {evt.replace('email.', '')}
              </StatusBadge>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-text-secondary">
            <span
              className={clsx(
                'flex items-center gap-1',
                webhook.active ? 'text-success' : 'text-text-tertiary',
              )}
            >
              <span
                className={clsx(
                  'inline-block h-2 w-2 rounded-full',
                  webhook.active ? 'bg-success' : 'bg-surface-3',
                )}
              />
              {webhook.active ? 'Active' : 'Inactive'}
            </span>
            {webhook.last_delivery_at && (
              <span>
                Last delivery:{' '}
                {formatDistanceToNow(new Date(webhook.last_delivery_at), { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border border-surface-3 px-3 py-1.5 text-sm hover:bg-surface-1"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onTest}
          disabled={isTesting}
          className="rounded-md border border-surface-3 px-3 py-1.5 text-sm hover:bg-surface-1 disabled:opacity-50"
        >
          {isTesting ? 'Testing...' : 'Test'}
        </button>
        <button
          type="button"
          onClick={onLogs}
          className="rounded-md border border-surface-3 px-3 py-1.5 text-sm hover:bg-surface-1"
        >
          Logs
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-md border border-surface-3 px-3 py-1.5 text-sm text-danger hover:bg-red-50"
        >
          Delete
        </button>
      </div>
      {testResult && <TestResultDisplay result={testResult} />}
    </div>
  )
}

function TestResultDisplay({ result }: { result: WebhookTestResult }): React.JSX.Element {
  return (
    <div
      className={clsx(
        'mt-3 rounded-lg p-3 text-sm',
        result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800',
      )}
    >
      <p className="font-medium">
        {result.success ? 'Test delivered successfully' : 'Test failed'}
      </p>
      <p>
        HTTP {result.status_code} in {result.response_time_ms}ms
      </p>
      {result.error && <p className="mt-1 font-mono text-xs">{result.error}</p>}
    </div>
  )
}

function EmptyWebhooks({ onAddClick }: { onAddClick: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <WebhookIcon className="mb-4 h-12 w-12 text-text-tertiary" />
      <h3 className="text-lg font-medium text-text-primary">No webhook endpoints</h3>
      <p className="mt-1 max-w-sm text-sm text-text-secondary">
        Add an endpoint to receive real-time event notifications.
      </p>
      <button
        type="button"
        onClick={onAddClick}
        className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Add your first endpoint
      </button>
    </div>
  )
}

function DeleteConfirmation({
  webhook,
  isPending,
  onConfirm,
  onCancel,
}: {
  webhook: Webhook
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-surface-0 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-text-primary">Delete Webhook</h3>
        <p className="mt-2 text-sm text-text-secondary">
          Are you sure you want to delete the webhook for {webhook.url}?
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}
